import React, {lazy, FC, Suspense, startTransition, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CompanyStandards from "./CompanyStandards";
import TribeSection from "./TribeSection";
import { kontentVariables, PATHS } from "../utils";
import SuccessStoriesSection from "./SuccessStoriesSection";
import useContentTypes from "../services/useContentTypes";
import ArrowRightBtn from "../assets/images/general/right_arrow_btn.png";
import MultiClientSlider from "../components/MultiClientSlider";
import ErrorBoundary from "../components/ErrorBoundary";
const WorldMap = lazy(() => import("../components/WorldMap"));
interface Props {
  data: any;
}

const Home: FC<Props> = ({ data }) => {
  const navigate = useNavigate();
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const { contentItems, loading } = useContentTypes(
    kontentVariables.serviceCategoriesContentType
  );
  const serviceCategories = contentItems ? contentItems?.slice(0, 4) : [];

  // Defer WorldMap loading to prevent suspension
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadMap(true);
    }, 1000); // Load map after 1 second

    return () => clearTimeout(timer);
  }, []);

  // Guard clause to prevent rendering with invalid data
  if (!data || loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <main id="main">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <section id="hero" className="hero-sec hero-padding home-top">
                <div className="container">
                  <div className="row gy-4">
                    <div className="col-lg-8 hero-header">
                      <div className="top-section">
                        <div className="section-header">
                          <h1>
                            {data?.elements?.crafting_a_better_world?.value.replace(
                              /(<([^>]+)>)/gi,
                              ""
                            )}
                          </h1>
                          <p>
                            {data?.elements?.all_your_technology?.value.replace(
                              /(<([^>]+)>)/gi,
                              ""
                            )}
                          </p>
                          <div className="form-group ">
                            <Link
                              to={
                                data?.elements?.say_hello_url?.value ||
                                PATHS.contactUs
                              }
                              className="btn btn-primary !text-left py-2 group home-btn"
                            >
                              {data?.elements?.say_hello?.value}{" "}
                              <span className="float-end ml-4 ">
                                <img loading="lazy" src={ArrowRightBtn} alt="ArrowRightBtn" />
                              </span>
                              <span className="clear-both"></span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="global-section ">
                    <img loading="lazy"
                      src={data?.elements?.top_right_image?.value[0]?.url}
                      className="img-fluid img-globe"
                      alt="globe"
                    />
                  </div>
                </div>
              </section>

              <section id="map" className="map-sec">
                <div className="container">
                  <div className="row mt-5 mt-sd-1 mt-med-1">
                    {/* Desktop: Map left, text right */}
                    <div className="col-lg-7 map-img d-none d-lg-block">
                      {shouldLoadMap ? (
                        <ErrorBoundary>
                          <Suspense fallback={<div>Loading map...</div>}>
                            <WorldMap />
                          </Suspense>
                        </ErrorBoundary>
                      ) : (
                        <div style={{height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <div>Loading map...</div>
                        </div>
                      )}
                    </div>
                    <div className="col-lg-5 d-none d-lg-block">
                      <div className="map-content content-width">
                        <h3>
                          {data?.elements?.zero_sales?.value.replace(
                            /(<([^>]+)>)/gi,
                            ""
                          )}
                        </h3>
                        <p>
                          {data?.elements?.we_are_an_industry_leading?.value.replace(
                            /(<([^>]+)>)/gi,
                            ""
                          )}
                        </p>
                      </div>
                    </div>
                    {/* Mobile/Tablet: Text above map, both full width */}
                    <div className="col-12 d-lg-none">
                      <div
                        className="map-content content-width"
                        style={{ marginBottom: 16 }}
                      >
                        <h3>
                          {data?.elements?.zero_sales?.value.replace(
                            /(<([^>]+)>)/gi,
                            ""
                          )}
                        </h3>
                        <p>
                          {data?.elements?.we_are_an_industry_leading?.value.replace(
                            /(<([^>]+)>)/gi,
                            ""
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="col-12 d-lg-none" style={{ padding: 0 }}>
                      {shouldLoadMap ? (
                        <ErrorBoundary>
                          <Suspense fallback={<div>Loading map...</div>}>
                            <WorldMap />
                          </Suspense>
                        </ErrorBoundary>
                      ) : (
                        <div style={{height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <div>Loading map...</div>
                        </div>
                      )}
                    </div>
                    <div className="col-lg-12">
                      <div className="slider-images-container">
                        <div className="client-slider mt-5 mb-4">
                          <MultiClientSlider
                            images={data?.elements?.client_slider_images?.value}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section
                id="services"
                className="services smt-8 mt-sd-2 mt-med-2 mt-ld-4"
              >
                <div className="container">
                  <div className="row gy-4">
                    <div className="col-lg-4 ">
                      <div className="service-header">
                        <div className="section-header">
                          <h2>
                            {data?.elements?.top_notch?.value.replace(
                              /(<([^>]+)>)/gi,
                              ""
                            )}
                          </h2>
                          <p>
                            {data?.elements?.premier_services_tailored?.value.replace(
                              /(<([^>]+)>)/gi,
                              ""
                            )}
                          </p>
                          <Link
                            to={PATHS.services}
                            className="btn btn-primary !text-left py-2 group top-notch-btn"
                          >
                            {data?.elements?.view_all_services?.value.replace(
                              /(<([^>]+)>)/gi,
                              ""
                            )}
                            <span className="float-end ml-4 ">
                              <img loading="lazy" src={ArrowRightBtn} alt="ArrowRightBtn" />
                            </span>
                            <span className="clear-both"></span>
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-8 mt-med-0">
                      <div className="all-services">
                        <div className="row">
                          {serviceCategories &&
                            serviceCategories.map((item: any, i: any) => (
                              <div className="col-lg-6 services-card" key={i}>
                                <div
                                  onClick={() =>
                                    startTransition(() => {
                                      navigate(PATHS.services, {
                                        state:
                                          item?.elements?.title?.value[0]?.name.replace(
                                            /(<([^>]+)>)/gi,
                                            ""
                                          ),
                                      });
                                    })
                                  }
                                  className="cursor-pointer"
                                >
                                  <img loading="lazy"
                                    src={item?.elements?.image?.value[0]?.url}
                                    className="img-fluid"
                                    alt="services-image"
                                  />
                                  <h4>
                                    {item?.elements?.title?.value[0]?.name.replace(
                                      /(<([^>]+)>)/gi,
                                      ""
                                    )}
                                  </h4>
                                  <p>
                                    {item?.elements?.description?.value.replace(
                                      /(<([^>]+)>)/gi,
                                      ""
                                    )}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <div className="mt-sd-2 mt-med-2">
                <CompanyStandards />
              </div>
              <div className="mt-sd-2 mt-med-2">
                <SuccessStoriesSection limit={4} />
              </div>
              <div className="form-group">
                <Link
                  to={PATHS.portfolio}
                  className="btn btn-primary !text-left py-2 mt-5 group view-projects-btn"
                >
                  View All Projects
                  <span className="float-end ml-4 ">
                    <img loading="lazy" src={ArrowRightBtn} alt="ArrowRightBtn" />
                  </span>
                  <span className="clear-both"></span>
                </Link>
              </div>
              <div className="mt-med-2">
                <TribeSection />
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Home;
